import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createConsent, updateConsent } from '~/states/modules/consent';

import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Template from './Template';

import { onClose } from '~/common/closeMessage';
import { useStyle } from '~/common/style';

const Options = () => {
	const dispatch = useDispatch();
	const {
		data: consent,
		loading,
		error,
	} = useSelector(({ consent }) => consent);
	const company = useSelector(({ company }) => company.data);
	const cookies = useSelector(({ cookies }) => cookies.data);
	const custom = useStyle();

	const [cookiesByCategory, setCookiesByCategory] = useState(null);
	const [triggered, setTriggered] = useState(false);

	// Format consents
	useEffect(() => {
		if (!loading && company) {
			if (consent && consent.consents) formatOptions(consent.consents);
			else cookies && formatOptions(cookies);
		}
	}, [consent, loading, company, cookies]);

	// Wait submit success to close and send dlConsent to parent
	useEffect(() => {
		if (triggered && !loading && !error && consent) {
			onClose();
			setTriggered(false);
		}
	}, [triggered, loading, error, consent, cookiesByCategory]);

	// Get if all cookies are active to state allChecked
	const getChecks = (options) => {
		let check = true;
		options.forEach((option) => {
			check = check && option.active;
		});
		return check;
	};

	/**
	 * Set CookiesByCategory using the options
	 * @param {*} options is an array that can be cookies or consents to be arranged
	 */
	const formatOptions = (options) => {
		const consentsByCategory = {};

		// Fill consents by category
		options.forEach((option) => {
			consentsByCategory[option.category.name] =
				consentsByCategory[option.category.name] || [];
			consentsByCategory[option.category.name].push(option);
		});

		const newCookiesByCategory = [];

		// Fill array of cookies by category to format expected by React structure
		for (const category in consentsByCategory) {
			newCookiesByCategory.push({
				name: category,
				label: consentsByCategory[category][0].category.label,
				allChecked: getChecks(consentsByCategory[category]),
				isOpen: false,
				cookies: consentsByCategory[category],
				description: consentsByCategory[category][0].category.description,
			});
		}

		setCookiesByCategory(newCookiesByCategory);
	};

	// Change if chevorn is open or closed
	const changeChevronStatus = (key) => {
		const newStatus = cookiesByCategory.map((category, index) =>
			key === index ? { ...category, isOpen: !category.isOpen } : category,
		);

		setCookiesByCategory(newStatus);
	};

	// Changes the active state from clicked cookie
	const changeSingleCookieCheck = (k, ind) => {
		const newCategories = cookiesByCategory.map((category, key) => {
			let allChecked = true;
			return key === k
				? {
						...category,
						cookies: category.cookies.map((cookie, index) => {
							const newCheck = cookie.active;
							if (index === ind) {
								allChecked = allChecked && !newCheck;
								return { ...cookie, active: !newCheck };
							}
							allChecked = allChecked && newCheck;
							return cookie;
						}),
						allChecked,
				  }
				: category;
		});
		setCookiesByCategory(newCategories);
	};

	// Changes all checks from certain category to the allChecked prop from parent
	const changeAllCookiesCheck = (i) => {
		const newCategories = cookiesByCategory.map((category, index) => {
			const newCheck = !category.allChecked;
			return i === index
				? {
						...category,
						allChecked: newCheck,
						cookies: category.cookies.map((cookie) => ({
							...cookie,
							active: newCheck,
						})),
				  }
				: category;
		});
		setCookiesByCategory(newCategories);
	};

	// Submit changes
	const saveChanges = () => {
		const consents = { companyId: company?.id, consents: [] };
		cookiesByCategory.forEach(({ cookies }) =>
			consents.consents.push(...cookies),
		);

		if (!consent?.id) {
			dispatch(createConsent(consents));
		} else {
			dispatch(updateConsent({ id: consent.id, ...consents }));
		}

		setTriggered(true);
	};

	return (
		<Template className="dl-cc-options">
			<form className="theme-form mega-form col-12">
				<div
					className="col fs-text p-0"
					style={{ height: '150px', overflowY: 'auto' }}
				>
					{cookiesByCategory?.map(
						(
							{ name, cookies, allChecked, isOpen, label, description },
							key,
						) => {
							const size = cookies.length;
							const readOnly =
								name === 'Necessary' ||
								name === 'Necessário' ||
								name === 'Functional' ||
								name === 'Funcional';

							return (
								<div
									key={key}
									className="col pt-2 pl-2 pb-2 mt-1 w-6 fs-text border border-1 border-secondary rounded"
								>
									<div className="row d-flex justify-content-between">
										<div>
											<span className="pl-3">{label}</span>
											<span
												className="pl-1 pr-1 pt-0 pb-0 ml-1 rounded-circle"
												style={{
													backgroundColor: '#d0d5db',
													color: custom.cookiebarBtnColor,
												}}
											>
												{size}
											</span>
											{custom.cookiebarDetailedCategorization ? (
												<span
													className="ml-2"
													style={{
														color: custom.cookiebarBtnColor,
													}}
													onClick={() => {
														changeChevronStatus(key);
													}}
												>
													{/* Verify if chevron is open to render it up or down */}
													{isOpen ? (
														<FontAwesomeIcon icon={faChevronUp} />
													) : (
														<FontAwesomeIcon icon={faChevronDown} />
													)}
												</span>
											) : null}
										</div>
										<div className="pr-3">
											<div className="form-row">
												<label className="form-switch mb-0">
													<input
														type="checkbox"
														checked={allChecked}
														onChange={() => changeAllCookiesCheck(key)}
														disabled={readOnly}
													/>
													<i></i>
												</label>
											</div>
										</div>
									</div>
									{/* Renders cookies list if chevron is open */}
									{isOpen && (
										<div className="mt-3">
											{description && (
												<>
													<div className="row mb-2">
														<div className="col fs-description">
															{description}
														</div>
													</div>
													<hr className="mt-1 mb-1" />
												</>
											)}
											{cookies?.map(({ name, active }, index) => {
												const barra =
													index < cookies.length - 1 ? (
														<hr className="mt-1 mb-1" />
													) : null;

												return (
													<div key={index}>
														<div className="pl-3 row d-flex justify-content-between">
															<span
																style={{
																	whiteSpace: 'nowrap',
																	overflow: 'hidden',
																	textOverflow: 'ellipsis',
																}}
																className="col-10 pl-1"
															>
																{name}
															</span>
															<div
																className="mr-0"
																style={{ paddingRight: '11px' }}
															>
																<div className="form-row">
																	<label className="form-switch col-2 mb-0">
																		<input
																			type="checkbox"
																			onChange={() =>
																				changeSingleCookieCheck(key, index)
																			}
																			checked={active}
																			disabled={readOnly}
																		/>
																		<i></i>
																	</label>
																</div>
															</div>
														</div>
														{barra}
													</div>
												);
											})}
										</div>
									)}
								</div>
							);
						},
					)}
				</div>
				<div className="d-flex justify-content-center">
					<button
						type="button"
						className="btn btn-block mt-4"
						style={{
							backgroundColor: custom.cookiebarBtnColor,
							color: custom.cookiebarBtnTxtColor,
						}}
						onClick={saveChanges}
					>
						Salvar Alterações
					</button>
				</div>
			</form>
		</Template>
	);
};

export default Options;
